import { useEffect, useState } from "react";
import { AlertTriangle, X, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export const PaymentWarningBanner = ({ onVisibilityChange }: { onVisibilityChange?: (visible: boolean, ready: boolean) => void }) => {
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [daysPastDue, setDaysPastDue] = useState(0);
  const [isSuspended, setIsSuspended] = useState(false);
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    onVisibilityChange?.(visible && !dismissed, ready);
  }, [visible, dismissed, ready, onVisibilityChange]);

  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    setVisible(false);
    setReady(false);
    setDismissed(false);
    setIsSuspended(false);
    const checkPaymentStatus = async () => {
      try {
        if (!user) return;

        const { data: subDetail } = await supabase
          .from("subscription_details")
          .select("status, failed_payment_at, show_payment_warning")
          .eq("user_id", user.id)
          .maybeSingle();

        if (cancelled || !subDetail) return;

        // Only show banner if show_payment_warning is true OR status is suspended
        if (subDetail.status === "suspended") {
          setIsSuspended(true);
          setVisible(true);
          if (subDetail.failed_payment_at) {
            const failedAt = new Date(subDetail.failed_payment_at);
            const days = Math.floor((Date.now() - failedAt.getTime()) / (24 * 60 * 60 * 1000));
            setDaysPastDue(days);
          } else {
            setDaysPastDue(30);
          }
          return;
        }

        if (subDetail.show_payment_warning && subDetail.failed_payment_at) {
          const failedAt = new Date(subDetail.failed_payment_at);
          const days = Math.floor((Date.now() - failedAt.getTime()) / (24 * 60 * 60 * 1000));
          if (days >= 14) {
            setDaysPastDue(days);
            setVisible(true);
          }
        }
      } catch (err) {
        console.error("PaymentWarningBanner check failed:", err);
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    const sessionDismissed = user && sessionStorage.getItem("payment_warning_dismissed");
    if (sessionDismissed) {
      setDismissed(true);
      setReady(true);
      return () => { cancelled = true; };
    }

    checkPaymentStatus();
    return () => { cancelled = true; };
  }, [user, loading]);

  if (!visible || dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("payment_warning_dismissed", "true");
  };

  return (
    <div className={`relative w-full px-4 py-3 ${
      isSuspended 
        ? "bg-destructive/10 border-b border-destructive/20" 
        : "bg-amber-50 border-b border-amber-200"
    }`}>
      <div className="max-w-5xl mx-auto flex items-center gap-3">
        <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
          isSuspended ? "text-destructive" : "text-amber-600"
        }`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${
            isSuspended ? "text-destructive" : "text-amber-800"
          }`}>
            {isSuspended
              ? "Uw account is geblokkeerd vanwege een openstaande betaling."
              : `Er staat nog een factuur open (${daysPastDue} dagen). Je kunt deze op je gemak voldoen; je account blijft gewoon werken.`
            }
          </p>
        </div>
        <Button
          size="sm"
          variant={isSuspended ? "destructive" : "default"}
          className="flex-shrink-0 h-8"
          onClick={() => navigate("/mijn-praktijk/instellingen")}
        >
          <CreditCard className="w-3.5 h-3.5 mr-1.5" />
          Direct betalen
        </Button>
        {!isSuspended && (
          <button
            onClick={handleDismiss}
            className="text-amber-500 hover:text-amber-700 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentWarningBanner;
